<template>
  <div :class="$style.Container">
    <div :class="$style.Field">
      <div :class="$style.Label">存储容量</div>
      <div :class="$style.Options" v-if="defaultOrderValue">
        <button type="button" :class="[$style.Option, $style.OptionActive]">
          {{ defaultOrderValue?.capacityGB }}GB
        </button>
      </div>
      <div :class="$style.Options" v-else>
        <button
          v-for="option in capacityOptions"
          :key="option.value"
          type="button"
          :class="[$style.Option, activeCapacity === option.value ? $style.OptionActive : null]"
          @click="selectCapacity(option.value)"
        >
          {{ option.label }}
        </button>
        <div
          :class="[
            $style.CustomInput,
            activeCapacity === CUSTOM_OPTION_VALUE ? $style.CustomInputActive : null,
          ]"
        >
          <input
            :value="customCapacity"
            inputmode="numeric"
            autocomplete="off"
            placeholder="请输入100整数倍"
            @focus="activateCustomCapacity"
            @input="handleCustomCapacityInput"
            @blur="commitCustomCapacity"
            @keyup.enter="commitCustomCapacity"
          />
          <span>GB</span>
        </div>
      </div>
    </div>

    <div :class="$style.Field" style="margin-top: 15px">
      <div :class="$style.Label">存储时长</div>
      <div :class="$style.Options" v-if="defaultOrderValue">
        <button type="button" :class="[$style.Option, $style.OptionActive]">
          {{ defaultMonthsFormat }}
        </button>
      </div>
      <div :class="$style.Options" v-else>
        <button
          v-for="option in durationOptions"
          :key="option.value"
          type="button"
          :class="[$style.Option, activeDuration === option.value ? $style.OptionActive : null]"
          @click="selectDuration(option.value)"
        >
          {{ option.label }}
        </button>
        <div
          :class="[
            $style.CustomInput,
            activeDuration === CUSTOM_OPTION_VALUE ? $style.CustomInputActive : null,
          ]"
        >
          <input
            :value="customDuration"
            inputmode="numeric"
            autocomplete="off"
            placeholder="请输入整数"
            @focus="activateCustomDuration"
            @input="handleCustomDurationInput"
            @blur="commitCustomDuration"
            @keyup.enter="commitCustomDuration"
          />
          <span>年</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { computed } from 'vue';
  import {
    CUSTOM_OPTION_VALUE,
    capacityOptions,
    durationOptions,
    StorageExpansionValue,
    useStorageExpansion,
  } from '../hooks/useStorageExpansion';

  const props = withDefaults(
    defineProps<{
      value?: StorageExpansionValue;
      defaultOrderValue?: null | StorageExpansionValue;
    }>(),
    {
      value: () => ({
        capacityGB: 100,
        months: 1,
      }),
      defaultOrderValue: () => null,
    }
  );

  const emits = defineEmits<{
    (event: 'update:value', value: StorageExpansionValue): void;
  }>();

  const {
    activeCapacity,
    activeDuration,
    customCapacity,
    customDuration,
    selectCapacity,
    selectDuration,
    activateCustomCapacity,
    activateCustomDuration,
    updateCustomCapacity,
    updateCustomDuration,
    commitCustomCapacity,
    commitCustomDuration,
  } = useStorageExpansion({
    value: props.value,
    onChange: (value) => {
      emits('update:value', value);
    },
  });

  const defaultMonthsFormat = computed(() => {
    const months = props.defaultOrderValue?.months;
    if (!months) {
      return;
    }
    // 能整除 12，显示年
    if (months % 12 === 0) {
      return `${months / 12}年`;
    }
    // 不能整除，显示月
    return `${months}个月`;
  });
  function handleCustomCapacityInput(event: Event) {
    updateCustomCapacity((event.target as HTMLInputElement).value);
  }

  function handleCustomDurationInput(event: Event) {
    updateCustomDuration((event.target as HTMLInputElement).value);
  }
</script>

<style lang="less" module>
  .Container {
    padding: 24px;
    border-radius: 15px;
    border: 1px solid #424242;
    background: linear-gradient(
        0deg,
        var(--colorFillQuaternary, rgba(255, 255, 255, 0.04)) 0%,
        var(--colorFillQuaternary, rgba(255, 255, 255, 0.04)) 100%
      ),
      var(--colorBgElevated, #1f1f1f);
  }

  .Label {
    margin-bottom: 8px;
    color: rgba(255, 255, 255, 0.85);
    font-size: 16px;
    font-weight: 600;
    line-height: 24px;
  }

  .Options {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }

  .Option {
    width: 90px;
    height: 38px;
    padding: 0;
    border: 1px solid transparent;
    border-radius: 4px;
    background: rgba(255, 255, 255, 0.12);
    color: var(--colorTextSecondary, rgba(255, 255, 255, 0.65));
    cursor: pointer;
    font-size: 16px;
    font-weight: 400;
    line-height: 22px;
    transition: border-color 0.2s, color 0.2s, background-color 0.2s;

    &:hover {
      border: 1px solid var(--colorPrimaryHover, #dc8647);
    }
  }

  .OptionActive {
    border: 1px solid var(--colorPrimaryHover, #dc8647);
    color: var(--colorPrimaryHover, #dc8647);
  }

  .CustomInput {
    display: flex;
    align-items: center;
    width: 206px;
    height: 38px;
    overflow: hidden;
    border: 1px solid rgba(66, 66, 66, 1);
    border-radius: 4px;
    background-color: rgba(31, 31, 31, 1);
    transition: border-color 0.2s;

    input {
      width: 100%;
      min-width: 0;
      height: 100%;
      padding: 0 18px;
      border: none;
      outline: none;
      background: transparent;
      color: rgba(255, 255, 255, 0.85);
      font-size: 16px;
      font-weight: 400;
      line-height: 22px;
      text-align: center;

      &::placeholder {
        color: rgba(255, 255, 255, 0.35);
        font-size: 13px;
      }
    }

    span {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 66px;
      height: 100%;
      flex-shrink: 0;
      background-color: rgba(66, 66, 66, 1);
      color: rgba(255, 255, 255, 0.65);
      font-size: 16px;
      font-weight: 400;
      line-height: 22px;
    }
  }

  .CustomInputActive {
    border-color: rgba(248, 135, 61, 1);
  }
</style>
